import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllDoctors, getAllBookings, getAllBills, getAllLabReports } from '../../api';
import { FaBars } from 'react-icons/fa';
import styles from './UserDashboard.module.css';
import logoImage from '../../assets/logo.jpg';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState({
    profile: '--',
    booking: '--',
    billing: '--',
    doctors: '--',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const userEmail = localStorage.getItem('userEmail');

        const [doctors, bookings, bills, labReports] = await Promise.all([
          getAllDoctors(),
          getAllBookings(userEmail),
          getAllBills(userEmail),
          getAllLabReports()
        ]);

        // Ensure all data is arrays and get their lengths safely
        const doctorsCount = Array.isArray(doctors) ? doctors.length : 0;
        const bookingsCount = Array.isArray(bookings) ? bookings.length : 0;
        const billsCount = Array.isArray(bills) ? bills.length : 0;
        const labReportsCount = Array.isArray(labReports) ? labReports.length : 0;

        setUserStats({
          profile: 'Active',
          booking: bookingsCount,
          billing: billsCount,
          doctors: doctorsCount,
          labReports: labReportsCount
        });
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Route protection: redirect to login if not authenticated
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('loggedIn') === 'true';
    const hasUserRole = localStorage.getItem('userRole') === 'user';

    console.log('Auth check - loggedIn:', isLoggedIn, 'userRole:', hasUserRole);

    if (!isLoggedIn || !hasUserRole) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    console.log('Logout initiated from UserDashboard');

    // Clear all possible authentication-related localStorage items
    const itemsToRemove = [
      'token',
      'loggedIn',
      'userEmail',
      'userRole',
      'userPassword',
      'userData',
      'adminLoggedIn',
      'authToken',
      'sessionToken'
    ];

    itemsToRemove.forEach(item => {
      if (localStorage.getItem(item)) {
        console.log(`Removing localStorage item: ${item}`);
        localStorage.removeItem(item);
      }
    });

    // Clear sessionStorage as well
    sessionStorage.clear();

    // Force a page reload to clear any React state
    console.log('Redirecting to login page');
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  if (loading) {
    return (
      <div className={styles.dashboardWrapper}>
        <div className={styles.mainContent}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Loading dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardWrapper}>
      {/* Mobile Menu Toggle */}
      <button
        type="button"
        className={styles.mobileMenuToggle}
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        <FaBars aria-hidden="true" focusable="false" />
      </button>

      {/* Sidebar Overlay */}
      <div
        className={`${styles.sidebarOverlay} ${sidebarOpen ? styles.open : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <div className={`${styles.sidebar} ${sidebarOpen ? styles.open : ''}`}>
        <div className={styles.logoContainer}>
          <img src={logoImage} alt="RUPSA Clinic Logo" className={styles.logo} />
        </div>
        <div className={styles.welcome}>Welcome User</div>
        <ul className={styles.menuListBullets}>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/user/doctors')}>Doctors List</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/user/booking-history')}>Booking History</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/user/billing-history')}>Billing History</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/user/lab-report-history')}>View Lab Report</li>
          <li className={styles.menuBulletItem} onClick={handleLogout}>Log Out</li>
        </ul>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}> RUPSA CLINIC Booking and Billing App</h1>

        {error && (
          <div style={{ color: 'red', textAlign: 'center', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className={styles.cardsGrid}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>My Profile</div>
            <div className={styles.cardNumber}>{userStats.profile}</div>
            <button className={styles.viewBtn} onClick={() => handleNavigation('/user/profile')}>View</button>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>My Booking</div>
            <div className={styles.cardNumber}>{userStats.booking}</div>
            <button
              className={styles.viewBtn}
              onClick={() => handleNavigation('/user/booking-history')}
            >
              View
            </button>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>My Billing</div>
            <div className={styles.cardNumber}>{userStats.billing}</div>
            <button
              className={styles.viewBtn}
              onClick={() => handleNavigation('/user/billing-history')}
            >
              View
            </button>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>Doctor List</div>
            <div className={styles.cardNumber}>{userStats.doctors}</div>
            <button className={styles.viewBtn} onClick={() => handleNavigation('/user/doctors')}>View</button>
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>Lab Reports</div>
            <div className={styles.cardNumber}>{userStats.labReports}</div>
            <button
              className={styles.viewBtn}
              onClick={() => handleNavigation('/user/lab-report-history')}
            >
              View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard; 
